import { createContext, useState, useContext, useEffect } from "react";
import { permissoes } from "constants/Permissoes";
import { URL_ORQUESTRADOR } from "shared/util/perfis/exportEnvs";
import { getTokenDecoded } from "shared/util/tokenFunctions";
import { FuncionalidadeCategoriaModuloDTO } from "types/dto/FuncionalidadeCategoriaModuloDTO";
import { Funcionalidade } from "types/funcionalidade/FuncionalidadeType";
import { PermissaoUsuarioDTO } from "types/permissoes/PermissaoUsuario";
import { TipoPermissao } from "constants/EnumTipoPermissao";
import { http } from "shared/util/http";
import { toast } from "react-hot-toast";
import { LoadingContext } from "context/LoadingContext";
import { UserContext } from "context/UserContext";

export type PermissoesContextProps = {
  usuarioPossuiPermissao: (funcionalidade: Funcionalidade, permissao: TipoPermissao) => boolean;
  usuarioPossuiAcessoTela: (funcionalidades: Funcionalidade[]) => boolean;
  usuarioPossuiAcessoTelaByFiltro: (funcionalidades: Funcionalidade[], filtro: string) => boolean;
  isPermissoesCarregadas: boolean;
};

type FuncionalidadeByPermissao = {
  funcionalidade: FuncionalidadeCategoriaModuloDTO | undefined;
  permissao: PermissaoUsuarioDTO | undefined;
};

const INITIAL_CONTEXT = {
  usuarioPossuiPermissao: () => false,
  usuarioPossuiAcessoTela: () => false,
  usuarioPossuiAcessoTelaByFiltro: () => false,
  isPermissoesCarregadas: false,
};

export const PermissoesContext = createContext<PermissoesContextProps>(INITIAL_CONTEXT);

export const PermissoesContextProvider = ({ children }: any) => {
  const [mapFuncionalidadeById, setMapFuncionalidadeById] = useState<Map<string, FuncionalidadeCategoriaModuloDTO>>(
    new Map<string, FuncionalidadeCategoriaModuloDTO>()
  );
  const [funcionalidadeByPermissaoUsuario, setFuncionalidadeByPermissaoUsuario] = useState<FuncionalidadeByPermissao[]>(
    []
  );
  const [isPermissoesCarregadas, setIsPermissoesCarregadas] = useState<boolean>(false);
  const { setLoading } = useContext(LoadingContext);
  const { usuarioLogado } = useContext(UserContext);

  useEffect(() => {
    async function buscarPermissoesUsuarios() {
      setLoading(true);
      await http
        .get(`${URL_ORQUESTRADOR}/api/funcionalidade-categoria-modulo/buscar-funcionalidades`)
        .then((response) => {
          setMapFuncionalidadeById(new Map(Object.entries(response.data ?? {})));
        })
        .catch((error) => toast.error("Erro ao buscar permissões do usuário."))
        .finally(() => {
          setLoading(false);
        });
    }
    if (usuarioLogado.id) {
      buscarPermissoesUsuarios();
    }
  }, [usuarioLogado.id]);

  useEffect(() => {
    const tokenDecoded = getTokenDecoded();
    const funcionalidadesUsuario = tokenDecoded?.permissoesGrupoAcesso;
    setIsPermissoesCarregadas(false);
    if (funcionalidadesUsuario) {
      let funcionalidadesByPermissoes = funcionalidadesUsuario
        .split(";")
        .map((funcionalidadeIdByPermissaoId: string) => {
          const [funcionalidadeId, permissaoId] = funcionalidadeIdByPermissaoId.split(".");
          const funcionalidade = mapFuncionalidadeById.get(funcionalidadeId);
          const permissao = permissoes.get(permissaoId);
          return { funcionalidade, permissao };
        });
      setFuncionalidadeByPermissaoUsuario(funcionalidadesByPermissoes);
    }
    if (mapFuncionalidadeById.size > 0) {
      setIsPermissoesCarregadas(true);
    }
  }, [mapFuncionalidadeById, usuarioLogado.id]);

  const usuarioPossuiPermissao = (nomeFuncionalidade: Funcionalidade, permissao: TipoPermissao): boolean => {
    return funcionalidadeByPermissaoUsuario.some((funcionalidadeByPermissaoUsuario) => {
      return (
        funcionalidadeByPermissaoUsuario.funcionalidade?.nome === nomeFuncionalidade &&
        funcionalidadeByPermissaoUsuario.permissao?.nome === permissao
      );
    });
  };

  const usuarioPossuiAcessoTela = (funcionalidades: Funcionalidade[]): boolean => {
    for (let funcionalidadeO of funcionalidades) {
      const usuarioPossuiAcessoTela = funcionalidadeByPermissaoUsuario.some(
        (funcionalidadeByPermissaoUsuario) => funcionalidadeByPermissaoUsuario.funcionalidade?.nome === funcionalidadeO
      );

      if (usuarioPossuiAcessoTela) {
        return true;
      }
    }
    return false;
  };

  const usuarioPossuiAcessoTelaByFiltro = (funcionalidades: Funcionalidade[], filtro: string): boolean => {
    if (!getTokenDecoded()[filtro]) {
      return false;
    } else {
      for (let funcionalidadeO of funcionalidades) {
        const usuarioPossuiAcessoTela = funcionalidadeByPermissaoUsuario.some(
          (funcionalidadeByPermissaoUsuario) =>
            funcionalidadeByPermissaoUsuario.funcionalidade?.nome === funcionalidadeO
        );

        if (usuarioPossuiAcessoTela) {
          return true;
        }
      }
      return false;
    }
  };

  const contextData: PermissoesContextProps = {
    usuarioPossuiPermissao,
    usuarioPossuiAcessoTela,
    usuarioPossuiAcessoTelaByFiltro,
    isPermissoesCarregadas: isPermissoesCarregadas,
  };

  return <PermissoesContext.Provider value={contextData}>{children}</PermissoesContext.Provider>;
};

export function usePermissoesContext() {
  return useContext(PermissoesContext);
}
